import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, Phone } from "lucide-react";
import { site, enabledServices } from "@/config/site";
import { Logo } from "@/components/Logo";
import { trackEvent } from "@/lib/analytics";

const navItems = [
  { to: "/", label: "Home" },
  { label: "Painting Services", dropdown: true },
  { to: "/projects", label: "Our Projects" },
  { to: "/about", label: "About Us" },
  { to: "/process", label: "Our Process" },
  { to: "/service-areas", label: "Service Areas" },
  { to: "/faqs", label: "FAQs" },
  { to: "/contact", label: "Contact" },
];

const linkCls = (isActive) =>
  `rounded-md px-3 py-2 text-sm font-medium transition-[color,background-color] duration-200 ${
    isActive ? "text-ember" : "text-slate-200 hover:text-white hover:bg-white/5"
  }`;

export function Header() {
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownRef = useRef(null);
  const panelRef = useRef(null);
  const burgerRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    setServicesOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!servicesOpen) return undefined;
    const onDoc = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setServicesOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setServicesOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [servicesOpen]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    document.body.style.overflow = "hidden";
    const burger = burgerRef.current;
    const panel = panelRef.current;
    const getFocusables = () =>
      panel ? Array.from(panel.querySelectorAll('a[href], button:not([disabled])')) : [];
    getFocusables()[0]?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") {
        setMobileOpen(false);
        return;
      }
      if (e.key !== "Tab") return;
      const items = getFocusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
      burger?.focus();
    };
  }, [mobileOpen]);

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-white/10 bg-slate-950/90 text-white shadow-lg backdrop-blur-xl" data-testid="site-header">
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[70] focus:rounded-md focus:bg-ember focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white"
          data-testid="skip-to-content"
        >
          Skip to content
        </a>
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
          <Link to="/" data-testid="header-logo" className="rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-ember">
            <Logo variant="light" />
          </Link>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="Main navigation">
            {navItems.map((item) =>
              item.dropdown ? (
                <div
                  key={item.label}
                  className="relative"
                  ref={dropdownRef}
                  onMouseEnter={() => setServicesOpen(true)}
                  onMouseLeave={() => setServicesOpen(false)}
                >
                  <button
                    type="button"
                    data-testid="nav-services-dropdown"
                    aria-expanded={servicesOpen}
                    aria-haspopup="true"
                    onClick={() => setServicesOpen(true)}
                    className={`inline-flex items-center gap-1 ${linkCls(location.pathname.startsWith("/services"))}`}
                  >
                    {item.label}
                    <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${servicesOpen ? "rotate-180" : ""}`} aria-hidden="true" />
                  </button>
                  {servicesOpen && (
                    <div className="absolute left-1/2 top-full z-50 w-[620px] -translate-x-1/2 pt-3" data-testid="services-dropdown-panel">
                      <div className="grid grid-cols-2 gap-1 rounded-2xl border border-white/10 bg-slate-950/95 p-4 shadow-2xl backdrop-blur-xl">
                        {enabledServices.map((s) => (
                          <Link
                            key={s.slug}
                            to={`/services/${s.slug}`}
                            data-testid={`nav-service-${s.slug}`}
                            className="rounded-lg px-3 py-2 text-sm text-slate-200 transition-[color,background-color] duration-200 hover:bg-white/5 hover:text-ember"
                          >
                            {s.name}
                          </Link>
                        ))}
                        <Link
                          to="/services"
                          data-testid="nav-service-all"
                          className="col-span-2 mt-1 rounded-lg border border-white/10 px-3 py-2 text-center text-sm font-semibold text-ember transition-colors duration-200 hover:bg-ember hover:text-white"
                        >
                          View All Painting Services
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <NavLink key={item.to} to={item.to} end={item.to === "/"} data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`} className={({ isActive }) => linkCls(isActive)}>
                  {item.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <a
              href={site.phoneHref}
              data-testid="header-phone-link"
              onClick={() => trackEvent("phone_click", { location: "header" })}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-full px-3 text-sm font-semibold text-slate-100 transition-colors duration-200 hover:text-ember"
            >
              <Phone className="h-4 w-4 text-ember" aria-hidden="true" />
              {site.phone}
            </a>
            <Link
              to="/estimate"
              data-testid="header-estimate-btn"
              className="inline-flex min-h-[44px] items-center rounded-full bg-ember px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-orange-900/40 transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-ember-dark"
            >
              Get a Free Estimate
            </Link>
          </div>

          <button
            type="button"
            ref={burgerRef}
            data-testid="mobile-menu-button"
            aria-expanded={mobileOpen}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            onClick={() => setMobileOpen(true)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-white/10 text-white transition-colors duration-200 hover:bg-white/5 lg:hidden"
          >
            <Menu className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </header>

      {mobileOpen &&
        createPortal(
          <div className="fixed inset-0 z-[60] lg:hidden" role="dialog" aria-modal="true" aria-label="Site menu">
            <button
              type="button"
              aria-label="Close menu"
              className="absolute inset-0 h-full w-full cursor-default bg-slate-950/70 backdrop-blur-sm"
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-menu-backdrop"
            />
            <div ref={panelRef} className="absolute right-0 top-0 flex h-full w-[86%] max-w-sm flex-col overflow-y-auto bg-slate-950 shadow-2xl" data-testid="mobile-menu-panel">
              <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
                <Logo variant="light" />
                <button
                  type="button"
                  data-testid="mobile-menu-close"
                  aria-label="Close menu"
                  onClick={() => setMobileOpen(false)}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-white/10 text-white transition-colors duration-200 hover:bg-white/5"
                >
                  <X className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
              <nav className="flex-1 px-3 py-4" aria-label="Mobile navigation">
                {navItems.map((item) =>
                  item.dropdown ? (
                    <div key={item.label}>
                      <button
                        type="button"
                        data-testid="mobile-services-toggle"
                        aria-expanded={mobileServicesOpen}
                        onClick={() => setMobileServicesOpen((v) => !v)}
                        className="flex w-full items-center justify-between rounded-lg px-3 py-3 text-left text-base font-medium text-slate-100 transition-colors duration-200 hover:bg-white/5"
                      >
                        {item.label}
                        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${mobileServicesOpen ? "rotate-180" : ""}`} aria-hidden="true" />
                      </button>
                      {mobileServicesOpen && (
                        <div className="mb-1 ml-3 border-l border-white/10 pl-3">
                          <Link to="/services" data-testid="mobile-service-all" className="block rounded-md px-3 py-2.5 text-sm font-semibold text-ember">
                            All Painting Services
                          </Link>
                          {enabledServices.map((s) => (
                            <Link
                              key={s.slug}
                              to={`/services/${s.slug}`}
                              data-testid={`mobile-service-${s.slug}`}
                              className="block rounded-md px-3 py-2.5 text-sm text-slate-300 transition-colors duration-200 hover:text-ember"
                            >
                              {s.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === "/"}
                      data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) =>
                        `block rounded-lg px-3 py-3 text-base font-medium transition-colors duration-200 ${
                          isActive ? "text-ember" : "text-slate-100 hover:bg-white/5"
                        }`
                      }
                    >
                      {item.label}
                    </NavLink>
                  )
                )}
              </nav>
              <div className="space-y-3 border-t border-white/10 p-5" style={{ paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom))" }}>
                <Link
                  to="/estimate"
                  data-testid="mobile-menu-estimate-btn"
                  className="flex min-h-[48px] items-center justify-center rounded-full bg-ember px-5 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-ember-dark"
                >
                  Get a Free Estimate
                </Link>
                <a
                  href={site.phoneHref}
                  data-testid="mobile-menu-phone"
                  onClick={() => trackEvent("phone_click", { location: "mobile-menu" })}
                  className="flex min-h-[48px] items-center justify-center gap-2 rounded-full border border-white/15 px-5 py-3 text-sm font-semibold text-slate-100 transition-colors duration-200 hover:border-ember"
                >
                  <Phone className="h-4 w-4 text-ember" aria-hidden="true" /> {site.phone}
                </a>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
